<template>
  <div class="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4 pt-40 pb-32">
    <div
      class="col-span-1 w-full h-fit border-2 border-(--main-color-500) rounded-lg p-4 ms-2"
    >
      <div
        class="grid grid-cols-7 justify-items-center md:justify-items-start items-center"
      >
        <div
          class="text-(--main-color-500) font-bold col-span-7 md:col-span-full mb-4 md:mb-0"
        >
          Filtre
        </div>
        <UInput
          v-model="searchTerm"
          color="neutral"
          highlight
          placeholder="Rechercher..."
          size="lg"
          class="w-full mt-0 md:mt-4 col-span-3 md:col-span-full pr-2 md:pr-0"
        />
        <div
          class="grid gap-2 md:gap-4 grid-cols-2 col-span-4 md:col-span-full w-full"
        >
          <UInput
            v-model="minPrice"
            color="neutral"
            highlight
            placeholder="Prix min"
            size="lg"
            class="w-full mt-0 md:mt-4"
            type="number"
          />
          <UInput
            v-model="maxPrice"
            color="neutral"
            highlight
            placeholder="Prix max"
            size="lg"
            class="w-full mt-0 md:mt-4"
            type="number"
          />
        </div>
        <UInputMenu
          v-model="selectedCategories"
          multiple
          :items="categories"
          color="neutral"
          highlight
          placeholder="Catégorie"
          class="w-full mt-4 col-span-7 md:col-span-full"
        />
        <div
          class="grid gap-2 md:gap-4 grid-cols-2 col-span-4 md:col-span-full w-full mt-4"
        >
          <UCheckbox v-model="isHalal" label="Halal" color="main" size="lg" />
          <UCheckbox v-model="isVegan" label="Vegan" color="main" size="lg" />
        </div>
      </div>
    </div>
    <div
      class="col-span-2 lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-hidden justify-items-center"
    >
      <MealFlipCard
        v-for="dessert in filteredDesserts"
        :key="dessert.id"
        :food="dessert"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Repas } from "~/models/repas";

const { $api } = useNuxtApp();
const categories = ref<string[]>([]);
const { errorToast } = useToastMessages();
const apiUrl = useRuntimeConfig().public.apiUrl;

// Filter states
const searchTerm = ref("");
const minPrice = ref("");
const maxPrice = ref("");
const selectedCategories = ref<string[]>([]);
const isHalal = ref(false);
const isVegan = ref(false);

const { data: desserts, refresh: refreshDesserts } = await useAsyncData("desserts", async () => {
  try {
    const response = await $api.get("/dessert");
    if (response && Array.isArray(response)) {
      const tmp: Repas[] = [];
      for (let i = 0; i < response.length; i++) {
        const item = response[i];
        tmp.push({
          id: item.id,
          price: item.price,
          name: item.name,
          image: getImageUrl(item.image, apiUrl),
          category: { ...(item.category || {}), name: "Desserts" },
          is_halal: item.is_halal,
          is_vegan: item.is_vegan,
          sauce_nb: 0,
          is_best_seller: false,
          content: [],
          menu_content: [],
          repas_category_content: [],
          ratings: [],
          group: "Desserts",
        });
      }
      return tmp;
    }
    return [];
  } catch (e) {
    console.error("[desserts] SSR/fetch error:", e);
    if (import.meta.client) {
      errorToast(
        "Erreur desserts",
        "Une erreur est survenue lors du chargement des desserts"
      );
    }
    return [];
  }
});

onMounted(async () => {
  if (!desserts.value || desserts.value.length === 0) {
    await refreshDesserts();
    populateCategories();
  }
});

// Populate categories from data
const populateCategories = () => {
  categories.value = [];
  if (!desserts.value) return;
  desserts.value.forEach((d) => {
    if (d.category && d.category.name && !categories.value.includes(d.category.name)) {
      categories.value.push(d.category.name);
    }
  });
};

if (desserts.value) {
  desserts.value.forEach((d) => {
    if (
      d.category &&
      d.category.name &&
      !categories.value.includes(d.category.name)
    ) {
      categories.value.push(d.category.name);
    }
  });
}

// Computed property for filtered desserts
const filteredDesserts = computed(() => {
  if (!desserts.value) return [];

  return desserts.value.filter((d) => {
    // Search filter
    if (
      searchTerm.value &&
      !d.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    ) {
      return false;
    }

    // Price filter
    if (minPrice.value && d.price < parseFloat(minPrice.value)) {
      return false;
    }
    if (maxPrice.value && d.price > parseFloat(maxPrice.value)) {
      return false;
    }

    // Category filter
    if (
      selectedCategories.value.length > 0 &&
      d.category &&
      !selectedCategories.value.includes(d.category.name)
    ) {
      return false;
    }

    // Halal/Vegan filters
    if (isHalal.value && !d.is_halal) {
      return false;
    }
    if (isVegan.value && !d.is_vegan) {
      return false;
    }

    return true;
  });
});
</script>
